import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useClickOutside from '@/hooks/useClickOutSide';

import * as S from './style';

const LANGUAGE_LABELS: Record<string, string> = {
	ko: 'KO',
	en: 'EN',
	zh: '中文',
};

const LanguageSwitcher = () => {
	const { i18n } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const currentLanguage = (i18n.resolvedLanguage ?? i18n.language ?? 'ko').slice(0, 2);
	const supportedLanguageOptions = Array.isArray(i18n.options.supportedLngs) ? i18n.options.supportedLngs : ['ko', 'en', 'zh'];
	const supportedLanguages = supportedLanguageOptions
		.filter((language: string): language is string => typeof language === 'string' && language !== 'cimode')
		.map((language: string) => language.slice(0, 2))
		.filter((language: string, index: number, languages: string[]) => languages.indexOf(language) === index);
	const currentLanguageLabel = useMemo(() => LANGUAGE_LABELS[currentLanguage] ?? currentLanguage.toUpperCase(), [currentLanguage]);

	useClickOutside(containerRef, () => setIsOpen(false));

	const changeLanguage = (language: string) => {
		void i18n.changeLanguage(language);
		setIsOpen(false);
	};

	return (
		<S.Container ref={containerRef}>
			<S.DropdownButton type="button" aria-haspopup="listbox" aria-expanded={isOpen} onClick={() => setIsOpen((prev) => !prev)}>
				<S.CurrentValue>{currentLanguageLabel}</S.CurrentValue>
				<S.ArrowIcon isOpen={isOpen}>
					<svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
						<path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</S.ArrowIcon>
			</S.DropdownButton>
			{isOpen && (
				<S.MenuList role="listbox" aria-label="Language selector">
					{supportedLanguages.map((language) => (
						<S.MenuItem
							key={language}
							type="button"
							role="option"
							active={currentLanguage === language}
							aria-selected={currentLanguage === language}
							onClick={() => changeLanguage(language)}
						>
							{LANGUAGE_LABELS[language] ?? language.toUpperCase()}
						</S.MenuItem>
					))}
				</S.MenuList>
			)}
		</S.Container>
	);
};

export default LanguageSwitcher;
